import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import {
  ClickAwayListener,
  FormHelperText,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppColors } from "../../util/AppColors";

const getOptionKey = (option) =>
  option?.value ?? option?.stockId ?? option?.id ?? option?.name ?? "";

const getOptionLabel = (option) => {
  if (option == null || option === "") return "";
  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }
  return option?.name ?? "";
};

const matchesSearch = (option, searchText) => {
  if (!searchText) return true;
  if (option?._searchText?.includes(searchText)) return true;
  return [
    option?.name,
    option?.tradeName,
    option?.productName,
    option?.ingredient,
    option?.serviceName,
    option?.variantName,
  ].some(
    (field) =>
      field != null && String(field).toLowerCase().includes(searchText),
  );
};

const resolveValue = (value, list = []) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;

  const match = (Array.isArray(list) ? list : []).find(
    (opt) =>
      opt?.name === value ||
      opt?.value === value ||
      String(opt?.stockId) === String(value),
  );
  if (match) return match;
  return { name: value, value };
};

/**
 * @param {"default" | "procedure"} variant
 * - default: Inventory-style object value
 * - procedure: accepts current string value style (name), resolves against list
 */
const SearchableSelect = ({
  list = [],
  value = null,
  handleChange,
  label = "",
  error = false,
  helperText = "",
  disabled = false,
  maxFilteredOptions = 100,
  backgroundColor = "transparent",
  variant = "default",
  isForProcedure = false,
  showQtyNearExpiry = false,
  plusBtn = false,
  searchOnly = false,
  mandatoryField = false,
}) => {
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const isProcedure = variant === "procedure";
  const resolvedValue = useMemo(
    () => resolveValue(value, list),
    [value, list],
  );
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(getOptionLabel(resolvedValue));
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [anchorWidth, setAnchorWidth] = useState(0);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    setAnchorWidth(containerRef.current.clientWidth);
  }, [open, inputValue]);

  useEffect(() => {
    if (!open) {
      setInputValue(getOptionLabel(resolvedValue));
    }
  }, [resolvedValue, open]);

  const searchableList = useMemo(
    () =>
      (Array.isArray(list) ? list : []).map((opt) => ({
        ...opt,
        value: opt?.stockId ?? opt?.value,
        _searchText:
          opt?._searchText ??
          `${opt?.name ?? ""} ${opt?.productName ?? ""} ${opt?.tradeName ?? ""}`.toLowerCase(),
      })),
    [list],
  );

  const filteredOptions = useMemo(() => {
    const searchText = inputValue?.trim()?.toLowerCase() ?? "";
    const source = searchableList;
    const limit = maxFilteredOptions ?? source.length;
    const results = [];
    const selectedKey = getOptionKey(resolvedValue);

    for (let i = 0; i < source.length && results.length < limit; i += 1) {
      const option = source[i];
      if (!matchesSearch(option, searchText)) continue;
      results.push(option);
    }

    if (
      resolvedValue &&
      selectedKey !== "" &&
      !results.some((opt) => getOptionKey(opt) === selectedKey) &&
      matchesSearch(resolvedValue, searchText)
    ) {
      results.unshift(resolvedValue);
      if (results.length > limit) results.length = limit;
    }

    return results;
  }, [searchableList, inputValue, maxFilteredOptions, resolvedValue]);

  const selectOption = (option) => {
    handleChange?.(null, option);
    setInputValue(getOptionLabel(option));
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    handleChange?.(null, null);
    setInputValue("");
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const addFreeText = () => {
    const text = inputValue?.trim() ?? "";
    if (!text) return;
    selectOption({ name: text, value: text });
  };

  const handleInputChange = (event) => {
    const nextValue = event?.target?.value ?? "";
    setInputValue(nextValue);
    setOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        selectOption(filteredOptions[highlightedIndex]);
      } else if (
        isProcedure &&
        !searchOnly &&
        filteredOptions.length === 0 &&
        inputValue?.trim()
      ) {
        addFreeText();
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setInputValue(getOptionLabel(resolvedValue));
    }
  };

  const showAddNew =
    isProcedure &&
    !searchOnly &&
    open &&
    filteredOptions.length === 0 &&
    Boolean(inputValue?.trim());

  const closeDropdown = () => {
    setOpen(false);
    setInputValue(getOptionLabel(resolvedValue));
    setHighlightedIndex(-1);
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (dropdownRef.current?.contains(event.target)) return;
        closeDropdown();
      }}
    >
      <div ref={containerRef} style={{ width: "100%" }}>
        <TextField
          fullWidth
          size="small"
          label={
            label
              ? mandatoryField
                ? `${label} *`
                : label
              : undefined
          }
          value={inputValue}
          disabled={disabled}
          error={error}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {resolvedValue ? (
                  <CloseIcon
                    sx={{
                      color: "action.active",
                      cursor: disabled ? "default" : "pointer",
                      fontSize: 18,
                      mr: 0.5,
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      clearSelection(event);
                    }}
                  />
                ) : null}
                <ArrowDropDownIcon
                  sx={{
                    color: "action.active",
                    cursor: disabled ? "default" : "pointer",
                    transform: open ? "rotate(180deg)" : "none",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    setOpen((prev) => !prev);
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ background: backgroundColor }}
        />

        <Popper
          open={open}
          anchorEl={containerRef.current}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "offset",
              options: { offset: [0, 4] },
            },
            {
              name: "flip",
              enabled: true,
              options: { fallbackPlacements: ["top-start"] },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
                altAxis: true,
                padding: 8,
              },
            },
          ]}
        >
          <Paper
            ref={dropdownRef}
            elevation={3}
            sx={{
              background: AppColors.white,
              minHeight: 0,
              maxHeight: 280,
              overflowY: "auto",
              overflowX: "hidden",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: "4px",
            }}
            style={{
              width: anchorWidth || containerRef.current?.clientWidth,
              minWidth: anchorWidth || containerRef.current?.clientWidth,
              maxWidth: anchorWidth || containerRef.current?.clientWidth,
            }}
          >
            {filteredOptions.length === 0 ? (
              showAddNew ? (
                <div
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addFreeText();
                  }}
                  style={{
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 14,
                  }}
                >
                  <span>{inputValue}</span>
                  {plusBtn ? (
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 20 }}
                    />
                  ) : (
                    <span style={{ color: AppColors.appPrimary }}>Add New</span>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: "10px 14px",
                    color: AppColors.gray ?? "#6b7280",
                    fontSize: 14,
                  }}
                >
                  No options
                </div>
              )
            ) : (
              filteredOptions.map((option, index) => {
                const optionKey = getOptionKey(option);
                const isSelected =
                  resolvedValue != null &&
                  getOptionKey(resolvedValue) === optionKey;
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={`${optionKey}-${index}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectOption(option);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      padding: "8px 14px",
                      cursor: "pointer",
                      background: isHighlighted
                        ? "#f5f5f5"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                      color: getOptionLabel(option)
                        ?.toLowerCase()
                        ?.includes("(clinic)")
                        ? AppColors.appPrimary
                        : "inherit",
                      fontSize: 14,
                    }}
                  >
                    <div className="font-medium fs14 black">
                      {getOptionLabel(option)}
                    </div>
                    {isProcedure || isForProcedure ? (
                      <>
                        {showQtyNearExpiry ? (
                          <div className="font-medium fs10 red2">
                            Available Qty: {option?.totalQty}
                          </div>
                        ) : null}
                        <div className="flex-row-ali-cen gap4 mt2">
                          {option?.totalQty <= 0 ? (
                            <div className="red-tag white-color font-medium fs10 con-fit">
                              Out of Stock
                            </div>
                          ) : option?.totalQty > 0 && option?.totalQty <= 10 ? (
                            <div className="yellow-tag white-color font-medium fs10 con-fit">
                              Low Stock
                            </div>
                          ) : null}
                          {showQtyNearExpiry && option?.isNearExpiry ? (
                            <div className="org-tag white-color font-medium fs10 con-fit">
                              Near Expiry
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              })
            )}
          </Paper>
        </Popper>

        {helperText && !open ? (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};

export default SearchableSelect;
